import { useEffect, useState } from "react";
import {
   Card,
   Table,
   Tag,
   Button,
   Drawer,
   Form,
   Select,
   Space,
   Descriptions,
   Badge,
   message,
   Modal
} from "antd";
import { useLocation } from "react-router-dom";
import {
   FacebookFilled,
   SyncOutlined,
   DisconnectOutlined
} from "@ant-design/icons";

import axios from "../../api/axios";
import { getAllUsers } from "../../services/usersApi";
import { getProjects } from "../../services/projectsApi";
import { updateFacebookForm, getFacebookIntegration, syncFacebookForms, disconnectFacebook,syncFacebookLeads } from "../../services/facebookApi";

const FacebookForms = () => {
   const location = useLocation();
   const [forms, setForms] = useState([]);
   const [loading, setLoading] = useState(false);
   const [editingForm, setEditingForm] = useState(null);
   const [drawerOpen, setDrawerOpen] = useState(false);
   const [projects, setProjects] = useState([]);
   const [users, setUsers] = useState([]);
   const [integration, setIntegration] = useState(null);
   const [syncing, setSyncing] = useState(false);
   const [disconnecting, setDisconnecting] = useState(false);
   const [syncingLeads, setSyncingLeads] = useState(false);
   const [form] = Form.useForm();

   useEffect(() => {

      const params = new URLSearchParams(location.search);

      if (params.get("connected") === "true") {

         message.success("Facebook connected successfully.");

         fetchIntegration();
         fetchForms();

         window.history.replaceState(
            {},
            "",
            location.pathname
         );

      }

   }, [location]);

   const fetchForms = async () => {

      try {

         setLoading(true);

         const res = await axios.get("/facebook/forms");

         setForms(res.data.data);

      } catch (error) {

         console.error(error);

      } finally {

         setLoading(false);

      }

   };

   const handleSyncLeads = async () => {
      try {
         setSyncingLeads(true);

         const res = await syncFacebookLeads(integration.page_id);

         message.success(
            `Leads synced: ${res.data.imported}, Duplicate: ${res.data.duplicate}`
         );

      } catch (error) {

         console.error(error);

         message.error(
            error.response?.data?.message ||
            "Failed to sync Facebook leads."
         );

      } finally {
         setSyncingLeads(false);
      }
   };

   useEffect(() => {

      fetchIntegration();
      fetchForms();
      fetchDropdownData();

   }, []);

   const handleEdit = (record) => {

      setEditingForm(record);

      form.setFieldsValue({

         projectId: record.project_id,

         assignedTo: record.assigned_to,

         leadStatus: record.lead_status

      });

      setDrawerOpen(true);

   };

   const fetchDropdownData = async () => {

      try {

         const [projectRes, userRes] = await Promise.all([
            getProjects(),
            getAllUsers()
         ]);

         setProjects(projectRes.data || []);
         setUsers(userRes.data || []);

      } catch (error) {

         console.error(error);

      }

   };

   const handleSave = async () => {

      try {

         const values = await form.validateFields();

         await updateFacebookForm(editingForm.id, values);

         message.success("Configuration updated successfully.");

         setDrawerOpen(false);

         fetchForms();

      } catch (error) {

         console.error(error);

         message.error("Failed to update configuration.");

      }

   };

   const fetchIntegration = async () => {

      try {

         const res = await getFacebookIntegration();

         setIntegration(res.data.data);

      } catch (error) {

         console.error(error);

      }

   };


   const handleSyncForms = async () => {

      try {

         setSyncing(true);

         const res = await syncFacebookForms();

         await Promise.all([
            fetchForms(),
            fetchIntegration()
         ]);

         message.success(
            res.data.message || "Forms synced successfully."
         );

      } catch (error) {

         console.error(error);

         message.error(
            error.response?.data?.message || "Failed to sync forms."
         );

      } finally {

         setSyncing(false);

      }

   };

   const handleDisconnect = () => {

      Modal.confirm({

         title: "Disconnect Facebook",

         content:
            "This will stop importing new Facebook leads. Are you sure you want to disconnect?",

         okText: "Disconnect",

         okButtonProps: {
            danger: true,
            loading: disconnecting
         },

         cancelText: "Cancel",

         onOk: async () => {

            try {

               setDisconnecting(true);

               const res = await disconnectFacebook();

               message.success(res.data.message);

               await Promise.all([
                  fetchIntegration(),
                  fetchForms()
               ]);

            } catch (error) {

               console.error(error);

               message.error(
                  error.response?.data?.message ||
                  "Failed to disconnect Facebook."
               );

            } finally {

               setDisconnecting(false);

            }

         }

      });

   };

   const handleConnectFacebook = () => {

      const user = JSON.parse(localStorage.getItem("user"));

      if (!user?.id) {
         return;
      }

      window.location.href =
         `${process.env.REACT_APP_API_URL}facebook/login?userId=${user.id}`;

   };

   const columns = [

      {
         title: "Form Name",
         dataIndex: "form_name"
      },

      {
         title: "Project",
         dataIndex: "project_name",
         render: (value) => value || "-"
      },

      {
         title: "Assigned To",
         dataIndex: "assigned_name",
         render: (value) => value || "-"
      },

      {
         title: "Lead Status",
         dataIndex: "lead_status",
         render: (status) => (
            <Tag color={getStatusColor(status)}>
               {status}
            </Tag>
         )
      },
      {
         title: "Action",
         key: "action",
         render: (_, record) => (
            <Button
               type="primary"
               size="small"
               onClick={() => handleEdit(record)}
            >
               Edit
            </Button>
         )
      }

   ];


   const getStatusColor = (status) => {

      switch (status) {

         case "New":
            return "blue";

         case "Contacted":
            return "orange";

         case "Qualified":
            return "green";

         case "Site Visit":
            return "cyan";

         case "Proposal Sent":
            return "purple";

         case "Booking":
            return "gold";

         case "Closed":
            return "success";

         case "Lost":
            return "red";

         default:
            return "default";
      }

   };

   return (
      <>

         <Card
            style={{ marginBottom: 20 }}
            title={
               <div>
                  <div
                     style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        fontWeight: 600,
                        fontSize: 16
                     }}
                  >
                     <FacebookFilled style={{ color: "#1877F2" }} />
                     Facebook Lead Ads Integration
                  </div>

                  <div
                     style={{
                        color: "#8c8c8c",
                        fontSize: 13,
                        marginTop: 4,
                        fontWeight: 400
                     }}
                  >
                     Manage your Facebook pages and automatically import leads.
                  </div>
               </div>
            }
         >

            {integration ? (

               <>
                  <Descriptions
                     bordered
                     column={1}
                     size="small"
                  >
                     <Descriptions.Item label="Page Name">
                        {integration.page_name}
                     </Descriptions.Item>

                     <Descriptions.Item label="Page ID">
                        {integration.page_id}
                     </Descriptions.Item>

                     <Descriptions.Item label="Connection Status">

                        <Badge
                           status="success"
                           text="Connected"
                        />

                     </Descriptions.Item>

                     <Descriptions.Item label="Webhook">

                        <Badge
                           status={
                              integration.webhook_subscribed
                                 ? "success"
                                 : "error"
                           }
                           text={
                              integration.webhook_subscribed
                                 ? "Subscribed"
                                 : "Not Subscribed"
                           }
                        />

                     </Descriptions.Item>

                     <Descriptions.Item label="Connected On">
                        {new Date(integration.connected_at).toLocaleString()}
                     </Descriptions.Item>

                  </Descriptions>

                  <Space
                     style={{
                        marginTop: 20
                     }}
                  >

                     <Button
                        type="primary"
                        icon={<SyncOutlined />}
                        loading={syncing}
                        disabled={syncing}
                        onClick={handleSyncForms}
                     >
                        Sync Forms
                     </Button>
                     <Button
                        type="primary"
                        icon={<SyncOutlined />}
                        loading={syncingLeads}
                        disabled={syncingLeads}
                        onClick={handleSyncLeads}
                     >
                        Sync Leads
                     </Button>

                     <Button
                        danger
                        icon={<DisconnectOutlined />}
                        loading={disconnecting}
                        onClick={handleDisconnect}
                     >
                        Disconnect
                     </Button>

                  </Space>

               </>

            ) : (

               <div
                  style={{
                     textAlign: "center",
                     padding: "40px 20px"
                  }}
               >
                  <FacebookFilled
                     style={{
                        fontSize: 50,
                        color: "#bfbfbf",
                        marginBottom: 16
                     }}
                  />

                  <h3>No Facebook Page Connected</h3>

                  <p style={{ color: "#888" }}>
                     Connect your Facebook Page to start importing leads automatically.
                  </p>

                  <Button
                     type="primary"
                     icon={<FacebookFilled />}
                     onClick={handleConnectFacebook}
                  >
                     Connect Facebook
                  </Button>

               </div>

            )}

         </Card>

         <Card title={`Facebook Forms (${forms.length})`}>

            {integration ? (
               <Table
                  rowKey="id"
                  loading={loading}
                  columns={columns}
                  dataSource={forms}
                  pagination={{
                     pageSize: 10,
                     hideOnSinglePage: true
                  }}
                  scroll={{
                     x: 800
                  }}
               />
            ) : (

               <div
                  style={{
                     textAlign: "center",
                     padding: 30,
                     color: "#999"
                  }}
               >
                  No Facebook forms available.
               </div>

            )}

            <Drawer
               title="Facebook Form Configuration"
               open={drawerOpen}
               width={450}
               onClose={() => setDrawerOpen(false)}
            >

               <Form
                  form={form}
                  layout="vertical"
               >

                  <Form.Item
                     label="Form"
                  >

                     <strong>
                        {editingForm?.form_name}
                     </strong>

                  </Form.Item>

                  <Form.Item
                     label="Project"
                     name="projectId"
                     rules={[
                        {
                           required: true,
                           message: "Please select a project"
                        }
                     ]}
                  >

                     <Select
                        placeholder="Select Project"
                        options={projects.map(project => ({
                           label: project.name,
                           value: project.id
                        }))}
                     />

                  </Form.Item>

                  <Form.Item
                     label="Assign To"
                     name="assignedTo"
                     rules={[
                        {
                           required: true,
                           message: "Please select a user"
                        }
                     ]}
                  >

                     <Select
                        placeholder="Select User"
                        options={users.map(user => ({
                           label: user.name,
                           value: user.id
                        }))}
                     />

                  </Form.Item>

                  <Form.Item
                     label="Lead Status"
                     name="leadStatus"
                     rules={[
                        {
                           required: true,
                           message: "Please select lead status"
                        }
                     ]}
                  >

                     <Select
                        options={[
                           { value: "New" },
                           { value: "Contacted" },
                           { value: "Qualified" },
                           { value: "Site Visit" },
                           { value: "Proposal Sent" },
                           { value: "Booking" },
                           { value: "Closed" },
                           { value: "Lost" }
                        ]}
                     />

                  </Form.Item>

                  <Space>

                     <div
                        style={{
                           display: "flex",
                           justifyContent: "flex-end",
                           gap: 10,
                           marginTop: 20
                        }}
                     >

                        <Button
                           onClick={() => setDrawerOpen(false)}
                        >
                           Cancel
                        </Button>

                        <Button
                           type="primary"
                           onClick={handleSave}
                        >
                           Save
                        </Button>

                     </div>
                  </Space>

               </Form>

            </Drawer>

         </Card>
      </>

   );

};

export default FacebookForms;